/*
 * The approval matrix, sensitive-field access, and the approvals inbox.
 *
 * Shapes follow ApprovalWorkflowController (with its Workflow/Rule/Step
 * resources), SensitiveFieldController and ApprovalController. Workflows are
 * CRM processes; their rules and steps are configuration and fully editable.
 */
import { users } from "@/mock/admin";
import { staffName } from "@/mock/masters";
import { approvals as workApprovals } from "@/mock/work";
import { roles } from "./access";
import { ok, created, fail, notFound, invalid, paginate, nextId, daysAgo, hoursAgo, now } from "./util";

/* ------------------------------------------------------------------ */
/* Workflows → rules → steps                                             */
/* ------------------------------------------------------------------ */

let stepSeq = 0;
const step = (name, role, sla_hours = 24, escalate_to = null) => ({
  id: ++stepSeq, name, approver_type: "role", approver_value: role, is_mandatory: true, sla_hours,
  escalate_after_hours: escalate_to ? sla_hours : null,
  escalate_to_type: escalate_to ? "role" : null,
  escalate_to_value: escalate_to,
});

let ruleSeq = 0;
const rule = (name, priority, conditions, steps, is_default = false) => ({
  id: ++ruleSeq, name, priority, conditions, is_default, is_active: true,
  steps: steps.map((s, i) => ({ ...s, sequence: i + 1 })),
});

const when = (field, operator, value) => ({ field, operator, value });

const workflows = [
  {
    id: 1, code: "quotation.discount", name: "Quotation discount", module: "Quotations",
    description: "A quotation priced below list needs sign-off before it is sent.",
    rules: [
      rule("Discount over 20%", 10, [when("discount_percent", ">", "20")], [step("Sales manager", "sales-manager", 24), step("Finance", "finance", 48, "management")]),
      rule("Discount 10–20%", 20, [when("discount_percent", ">=", "10")], [step("Sales manager", "sales-manager", 24, "management")]),
      rule("Up to 10%", 100, [], [], true),
    ],
  },
  {
    id: 2, code: "contract.approval", name: "Contract approval", module: "Contracts",
    description: "A contract is approved before it goes to the customer for signature.",
    rules: [
      rule("Contract value over KES 2M", 10, [when("contract_value", ">", "2000000")], [step("Finance", "finance", 48), step("Management", "management", 72)]),
      rule("Non-standard terms", 20, [when("has_custom_terms", "=", "true")], [step("Finance", "finance", 48), step("CTO/Admin", "admin", 48)]),
      rule("Standard contract", 100, [], [step("Finance", "finance", 48)], true),
    ],
  },
  {
    id: 3, code: "renewal.price_change", name: "Renewal price change", module: "Renewals",
    description: "A renewal priced differently from the current contract.",
    rules: [
      rule("Price decrease", 10, [when("change_percent", "<", "0")], [step("Customer success lead", "customer-success", 24), step("Finance", "finance", 48)]),
      rule("Increase above 15%", 20, [when("change_percent", ">", "15")], [step("Management", "management", 48)]),
      rule("Any other change", 100, [], [step("Customer success lead", "customer-success", 24)], true),
    ],
  },
  {
    id: 4, code: "customer.exit", name: "Customer exit", module: "Retention",
    description: "Closing a customer's account: data handover, final invoice and the reason recorded.",
    rules: [
      rule("Key account", 10, [when("segment", "in", "Enterprise,Banking & Finance")], [step("Customer success lead", "customer-success", 24), step("Finance", "finance", 48), step("Management", "management", 72)]),
      rule("Every other exit", 100, [], [step("Customer success lead", "customer-success", 24), step("Finance", "finance", 48)], true),
    ],
  },
  {
    id: 5, code: "opportunity.close_lost", name: "Closing a large deal as lost", module: "Opportunities",
    description: "A lost deal above the threshold is reviewed before the reason is final.",
    rules: [
      rule("Deal value over KES 1M", 10, [when("amount", ">", "1000000")], [step("Sales manager", "sales-manager", 48)]),
      rule("Smaller deals", 100, [], [], true),
    ],
  },
  {
    id: 6, code: "ticket.sla_waiver", name: "Ticket SLA waiver", module: "Support", is_active: false,
    description: "Stopping the SLA clock on a ticket while the customer is unreachable.",
    rules: [rule("Any waiver", 100, [], [step("Support lead", "support", 8)], true)],
  },
  {
    id: 7, code: "data.export", name: "Bulk data export", module: "Reporting",
    description: "Exports of customer or contact data above the row threshold.",
    rules: [
      rule("Customer or contact data", 10, [when("export_type", "in", "customers,contacts")], [step("CTO/Admin", "admin", 24), step("Audit & risk", "audit-risk", 24)]),
      rule("Other exports", 100, [], [], true),
    ],
  },
  {
    id: 8, code: "role.permission_change", name: "Role permission change", module: "System Administration",
    description: "Widening or narrowing what a role may do.",
    rules: [rule("Every change", 100, [], [step("Head of Sales", "management", 24), step("CTO/Admin", "admin", 24)], true)],
  },
  {
    id: 9, code: "role.assignment_change", name: "Role assignment change", module: "System Administration",
    description: "Giving somebody a role, or taking one away.",
    rules: [rule("Every change", 100, [], [step("Head of Sales", "management", 24), step("CTO/Admin", "admin", 24)], true)],
  },
].map((w) => ({ is_active: true, ...w }));

const presentStep = (s) => ({
  id: s.id, sequence: s.sequence, name: s.name, approver_type: s.approver_type, approver_value: s.approver_value,
  is_mandatory: s.is_mandatory, sla_hours: s.sla_hours, escalate_after_hours: s.escalate_after_hours,
  escalate_to_type: s.escalate_to_type, escalate_to_value: s.escalate_to_value,
});

const presentRule = (r) => ({
  id: r.id, name: r.name, priority: r.priority, conditions: r.conditions ?? [], is_default: r.is_default, is_active: r.is_active,
  steps: [...r.steps].sort((a, b) => a.sequence - b.sequence).map(presentStep),
});

const presentWorkflow = (w) => ({
  id: w.id, code: w.code, name: w.name, module: w.module, description: w.description, is_active: w.is_active,
  // Every sample process has something attached that applies its outcome.
  applies_changes: true,
  rules: [...w.rules].sort((a, b) => a.priority - b.priority).map(presentRule),
});

const findWorkflow = (id) => workflows.find((w) => String(w.id) === String(id));
const findRule = (workflowId, ruleId) => findWorkflow(workflowId)?.rules.find((r) => String(r.id) === String(ruleId));

const ruleFields = (body, current = {}) => ({
  name: body.name ?? current.name,
  priority: body.priority !== undefined ? Number(body.priority) || 100 : current.priority ?? 100,
  is_default: body.is_default !== undefined ? !!body.is_default : current.is_default ?? false,
  is_active: body.is_active !== undefined ? !!body.is_active : current.is_active ?? true,
  conditions: Array.isArray(body.conditions) ? body.conditions : current.conditions ?? [],
});

const STEP_KEYS = ["name", "sequence", "approver_type", "approver_value", "is_mandatory", "sla_hours", "escalate_after_hours", "escalate_to_type", "escalate_to_value"];

const stepErrors = (body) => {
  const errors = {};
  if (!String(body.name ?? "").trim()) errors.name = ["Name the step."];
  if (!["role", "user"].includes(body.approver_type)) errors.approver_type = ["Choose who approves this step."];
  else if (!body.approver_value) errors.approver_value = ["Choose which one."];
  return Object.keys(errors).length ? errors : null;
};

const SELF_APPROVAL = {
  escalate: "Passed to somebody else who can approve it",
  skip: "Skipped — the next approver in the chain decides",
  allowed: "Allowed — they approve it themselves",
};

const settings = { self_approval: "escalate" };

const settingsPayload = () => ({
  settings: { self_approval: settings.self_approval },
  self_approval_options: Object.entries(SELF_APPROVAL).map(([value, label]) => ({ value, label })),
});

/* ------------------------------------------------------------------ */
/* Sensitive fields                                                       */
/* ------------------------------------------------------------------ */

const sensitiveFields = [
  { id: 1, module: "contracts", field_key: "contract_value", label: "Contract value", classification: "confidential", mask_strategy: "full" },
  { id: 2, module: "contracts", field_key: "payment_terms", label: "Payment terms", classification: "internal", mask_strategy: "full" },
  { id: 3, module: "contacts", field_key: "email", label: "Contact email", classification: "internal", mask_strategy: "email" },
  { id: 4, module: "contacts", field_key: "phone", label: "Contact phone", classification: "internal", mask_strategy: "partial" },
  { id: 5, module: "customers", field_key: "kra_pin", label: "Customer KRA PIN", classification: "confidential", mask_strategy: "partial" },
  { id: 6, module: "customers", field_key: "bank_account", label: "Customer bank account", classification: "restricted", mask_strategy: "partial" },
  { id: 7, module: "customers", field_key: "annual_revenue", label: "Customer annual revenue", classification: "confidential", mask_strategy: "full" },
  { id: 8, module: "quotations", field_key: "discount_percent", label: "Quotation discount %", classification: "confidential", mask_strategy: "full" },
  { id: 9, module: "quotations", field_key: "margin", label: "Quotation margin", classification: "restricted", mask_strategy: "hidden" },
  { id: 10, module: "users", field_key: "date_of_birth", label: "Staff date of birth", classification: "restricted", mask_strategy: "year_only" },
].map((f) => ({ is_active: true, ...f }));

// field_key → role names that may see it; the administrator always may.
const fieldAccess = {
  contract_value: ["admin", "finance", "management", "sales-manager", "audit-risk"],
  payment_terms: ["admin", "finance", "management", "customer-success", "audit-risk"],
  email: ["admin", "sales", "sales-manager", "customer-success", "implementation", "support", "marketing", "management"],
  phone: ["admin", "sales", "sales-manager", "customer-success", "implementation", "support", "management"],
  kra_pin: ["admin", "finance", "audit-risk"],
  bank_account: ["admin", "finance"],
  annual_revenue: ["admin", "sales-manager", "finance", "management"],
  discount_percent: ["admin", "sales-manager", "finance", "management", "audit-risk"],
  margin: ["admin", "finance", "management"],
  date_of_birth: ["admin"],
};

const presentField = (f) => ({ ...f, permission: `field.${f.module}.${f.field_key}.view` });

const sortedRoles = () => [...roles].sort((a, b) => a.name.localeCompare(b.name));

/* ------------------------------------------------------------------ */
/* Approval requests (inbox / mine)                                       */
/* ------------------------------------------------------------------ */

const workflowRef = (code) => {
  const w = workflows.find((x) => x.code === code);
  return { code: w.code, name: w.name, module: w.module };
};

const action = (id, sequence, step_name, approver, act, acted_ago = null, comments = null) => ({
  id, sequence, step_name, approver, action: act, comments,
  due_at: hoursAgo(-24 + sequence), is_overdue: false, acted_at: acted_ago === null ? null : hoursAgo(acted_ago),
});

const requests = [
  {
    id: 1, reference: "APR-2026-0131", status: "pending", workflow: workflowRef("quotation.discount"), rule: "Discount over 20%",
    subject: { type: "Quotation", id: 12, label: "QUO-0412 · Lakeview SACCO — HR & Payroll Enterprise" },
    payload: { discount_percent: 22, list_price: 1450000, quoted_price: 1131000 }, effective_date: null,
    reason: "Competing with Workpay; customer signs this month if we match.", requested_by: "Ann Mutua", current_sequence: 1,
    steps: [action(1, 1, "Sales manager", "Jane Wanjiku", "pending"), action(2, 2, "Finance", "Mercy Wambui", "pending")],
    outcome_note: null, can_return: true, completed_at: null, applied_at: null, created_at: hoursAgo(6),
  },
  {
    id: 2, reference: "APR-2026-0128", status: "pending", workflow: workflowRef("contract.approval"), rule: "Contract value over KES 2M",
    subject: { type: "Contract", id: 7, label: "CTR-0088 · Harambee Sacco Society — 3-year renewal" },
    payload: { contract_value: 2640000, term_months: 36 }, effective_date: null,
    reason: "Three-year renewal at the enterprise tier.", requested_by: "Grace Njeri", current_sequence: 2,
    steps: [action(3, 1, "Finance", "Mercy Wambui", "approved", 20, "Payment terms checked."), action(4, 2, "Management", "Jane Wanjiku", "pending")],
    outcome_note: null, can_return: true, completed_at: null, applied_at: null, created_at: daysAgo(2),
  },
  {
    id: 3, reference: "APR-2026-0119", status: "approved", workflow: workflowRef("customer.exit"), rule: "Every other exit",
    subject: { type: "Customer", id: 41, label: "Kilimani Dental Clinic" },
    payload: { reason_code: "EXIT-BUDGET", final_invoice: 45000 }, effective_date: daysAgo(-14).slice(0, 10),
    reason: "Closed the second branch; budget cut.", requested_by: "Jane Wanjiku", current_sequence: 2,
    steps: [action(5, 1, "Customer success lead", "Grace Njeri", "approved", 120), action(6, 2, "Finance", "Mercy Wambui", "approved", 96)],
    outcome_note: null, can_return: false, completed_at: daysAgo(4), applied_at: daysAgo(4), created_at: daysAgo(6),
  },
];

/*
 * The approvals the rest of the prototype tells (mock/work.js: My Work, the
 * records), in the same shape, so the inbox, My requests and All agree with
 * them. The signed-in person is Jane Wanjiku.
 */
const ME_NAME = "Jane Wanjiku";
const WORKFLOW_OF = {
  discount: { code: "quotation.discount", name: "Quotation discount", module: "Quotations" },
  proposal: { code: "proposal.approval", name: "Proposal approval", module: "Opportunities" },
  reassign: { code: "prospect.reassignment", name: "Prospect mass reassignment", module: "Prospects" },
  exit: { code: "customer.exit", name: "Customer exit", module: "Retention" },
  temp_access: { code: "access.temporary", name: "Temporary Access", module: "System Administration" },
  role_change: { code: "role.assignment_change", name: "Role Assignment Change", module: "System Administration" },
  onboarding_close: { code: "onboarding.close", name: "Onboarding close", module: "Onboarding" },
  config_change: { code: "records.bulk_reassign", name: "Configuration change", module: "Settings" },
};
let actionSeq = 100;
workApprovals.forEach((a) => {
  const current = a.current_step;
  requests.push({
    id: 100 + a.id, reference: a.code, status: a.status, workflow: WORKFLOW_OF[a.type], rule: null,
    subject: { type: a.record?.kind ?? "Request", id: a.record?.id ?? a.id, label: a.record ? `${a.record.code} · ${a.record.label}` : a.summary },
    payload: Object.fromEntries((a.detail ?? []).map(([k, v]) => [k, v])), effective_date: null, reason: a.summary,
    requested_by: staffName(a.requester_id), current_sequence: current,
    steps: a.chain.map((st, i) => ({
      id: ++actionSeq, sequence: i + 1, step_name: st.role, approver: staffName(st.approver_id),
      action: st.status === "waiting" ? "pending" : st.status, comments: st.comment || null,
      due_at: i + 1 === current && a.status === "pending" ? new Date(Date.now() + (a.sla_hours - a.waiting_hours) * 36e5).toISOString() : null,
      is_overdue: i + 1 === current && a.status === "pending" && a.waiting_hours > a.sla_hours,
      acted_at: st.at ? `${st.at}T12:00:00.000Z` : null,
    })),
    outcome_note: null, can_return: true, completed_at: a.status === "pending" ? null : new Date().toISOString(), applied_at: null,
    created_at: new Date(a.submitted_at).toISOString(),
  });
});

const presentRequest = (r) => r;
const currentStep = (r) => r.steps.find((st) => st.sequence === r.current_sequence);
const waitingOnMe = (r) => r.status === "pending" && currentStep(r)?.approver === ME_NAME && currentStep(r)?.action === "pending";
const pageOf = (list, query) => {
  const { data, ...page } = paginate(list.map(presentRequest), query);
  return ok({ data, links: {}, meta: { current_page: page.current_page, from: page.from, last_page: page.last_page, per_page: page.per_page, to: page.to, total: page.total } });
};
const findRequest = (id) => requests.find((r) => String(r.id) === String(id));

const decide = (id, outcome, comments, message) => {
  const r = findRequest(id);
  if (!r) return notFound(`Approval request ${id} was not found.`);
  if (r.status !== "pending") return fail("This request has already been decided.", { code: "APPROVAL_CLOSED" }, 409);

  const current = r.steps.find((s) => s.action === "pending");
  if (current && outcome !== "cancelled") {
    current.action = outcome === "approved" ? "approved" : outcome === "returned" ? "returned" : "rejected";
    current.comments = comments || null;
    current.acted_at = now();
  }
  const more = r.steps.some((s) => s.action === "pending");
  if (outcome === "approved" && more) {
    r.current_sequence += 1;
    return ok(presentRequest(r), "Approved — passed to the next approver");
  }
  r.status = outcome;
  r.completed_at = now();
  if (outcome === "approved") r.applied_at = now();
  return ok(presentRequest(r), message);
};

const activeUsers = () => users.filter((u) => u.status === "active").sort((a, b) => a.name.localeCompare(b.name));

/* ------------------------------------------------------------------ */
/* Routes                                                                 */
/* ------------------------------------------------------------------ */

export default [
  // ---- Approval matrix ----
  ["get", "tenant/config/approval-meta", () =>
    ok({
      approver_types: [
        { key: "role", label: "Anyone holding a role" },
        { key: "user", label: "A named person" },
      ],
      types_needing_value: ["role", "user"],
      operators: [
        { key: "=", label: "is" },
        { key: "!=", label: "is not" },
        { key: ">", label: "is greater than" },
        { key: ">=", label: "is at least" },
        { key: "<", label: "is less than" },
        { key: "<=", label: "is at most" },
        { key: "in", label: "is one of" },
        { key: "not_in", label: "is not one of" },
        { key: "contains", label: "contains" },
        { key: "is_set", label: "is provided" },
        { key: "is_empty", label: "is blank" },
      ],
      roles: sortedRoles().map(({ name, label }) => ({ name, label })),
      users: activeUsers().map((u) => ({ id: u.id, label: u.name })),
    })],

  ["get", "tenant/config/approval-settings", () => ok(settingsPayload())],

  ["put", "tenant/config/approval-settings", ({ body }) => {
    if (!SELF_APPROVAL[body.self_approval]) return invalid({ self_approval: ["Choose what happens when somebody would approve their own request."] });
    settings.self_approval = body.self_approval;
    return ok({ ...settingsPayload(), label: "Approval settings" }, "Saved. It applies to requests raised from now on; ones already waiting keep their approvers.");
  }],

  ["get", "tenant/config/approval-workflows", () =>
    ok([...workflows].sort((a, b) => a.module.localeCompare(b.module) || a.name.localeCompare(b.name)).map(presentWorkflow))],

  ["put", "tenant/config/approval-workflows/:id", ({ params, body }) => {
    const w = findWorkflow(params.id);
    if (!w) return notFound(`Approval workflow ${params.id} was not found.`);
    if (body.name !== undefined) w.name = body.name;
    if (body.description !== undefined) w.description = body.description;
    if (body.is_active !== undefined) w.is_active = !!body.is_active;
    return ok(presentWorkflow(w), "Workflow updated");
  }],

  ["post", "tenant/config/approval-workflows/:id/rules", ({ params, body }) => {
    const w = findWorkflow(params.id);
    if (!w) return notFound(`Approval workflow ${params.id} was not found.`);
    if (!String(body.name ?? "").trim()) return invalid({ name: ["Name the rule."] });
    const r = { id: ++ruleSeq, ...ruleFields(body), steps: [] };
    w.rules.push(r);
    return created(presentRule(r), "Rule added");
  }],

  ["put", "tenant/config/approval-workflows/:id/rules/:ruleId", ({ params, body }) => {
    const r = findRule(params.id, params.ruleId);
    if (!r) return notFound(`Approval rule ${params.ruleId} was not found.`);
    if (body.name !== undefined && !String(body.name).trim()) return invalid({ name: ["Name the rule."] });
    Object.assign(r, ruleFields(body, r));
    return ok(presentRule(r), "Rule updated");
  }],

  ["delete", "tenant/config/approval-workflows/:id/rules/:ruleId", ({ params }) => {
    const w = findWorkflow(params.id);
    const r = findRule(params.id, params.ruleId);
    if (!w || !r) return notFound(`Approval rule ${params.ruleId} was not found.`);
    w.rules.splice(w.rules.indexOf(r), 1);
    return ok(null, "Rule removed");
  }],

  ["post", "tenant/config/approval-workflows/:id/rules/:ruleId/steps", ({ params, body }) => {
    const r = findRule(params.id, params.ruleId);
    if (!r) return notFound(`Approval rule ${params.ruleId} was not found.`);
    const errors = stepErrors(body);
    if (errors) return invalid(errors);

    const s = { id: ++stepSeq, is_mandatory: true, sla_hours: null, escalate_after_hours: null, escalate_to_type: null, escalate_to_value: null };
    STEP_KEYS.forEach((k) => body[k] !== undefined && (s[k] = body[k]));
    s.sequence = Number(body.sequence) || Math.max(0, ...r.steps.map((x) => x.sequence)) + 1;
    r.steps.push(s);
    return created(presentStep(s), "Step added");
  }],

  ["put", "tenant/config/approval-workflows/:id/rules/:ruleId/steps/:stepId", ({ params, body }) => {
    const s = findRule(params.id, params.ruleId)?.steps.find((x) => String(x.id) === String(params.stepId));
    if (!s) return notFound(`Approval step ${params.stepId} was not found.`);
    const errors = stepErrors({ ...s, ...body });
    if (errors) return invalid(errors);
    STEP_KEYS.forEach((k) => body[k] !== undefined && (s[k] = body[k]));
    return ok(presentStep(s), "Step updated");
  }],

  ["delete", "tenant/config/approval-workflows/:id/rules/:ruleId/steps/:stepId", ({ params }) => {
    const r = findRule(params.id, params.ruleId);
    const s = r?.steps.find((x) => String(x.id) === String(params.stepId));
    if (!s) return notFound(`Approval step ${params.stepId} was not found.`);
    r.steps.splice(r.steps.indexOf(s), 1);
    // Close the gap so sequences stay contiguous.
    r.steps.sort((a, b) => a.sequence - b.sequence).forEach((x, i) => (x.sequence = i + 1));
    return ok(null, "Step removed");
  }],

  // ---- Sensitive fields ----
  ["get", "tenant/config/sensitive-fields", () => {
    const fields = [...sensitiveFields].sort((a, b) => a.module.localeCompare(b.module) || a.label.localeCompare(b.label));
    const list = sortedRoles();
    return ok({
      fields: fields.map(presentField),
      roles: list.map((r) => ({ name: r.name, label: r.label ?? r.name })),
      matrix: Object.fromEntries(fields.map((f) => [f.field_key, Object.fromEntries(list.map((r) => [r.name, (fieldAccess[f.field_key] ?? []).includes(r.name)]))])),
      classifications: ["public", "internal", "confidential", "restricted"],
      mask_strategies: [
        { key: "full", label: "Hide completely (*****)" },
        { key: "partial", label: "Show last four (*****1234)" },
        { key: "email", label: "Obscure email (a***@b***)" },
        { key: "year_only", label: "Year only (1990)" },
        { key: "hidden", label: "Omit entirely" },
      ],
    });
  }],

  ["put", "tenant/config/sensitive-fields/:id", ({ params, body }) => {
    const f = sensitiveFields.find((x) => String(x.id) === String(params.id));
    if (!f) return notFound(`Sensitive field ${params.id} was not found.`);
    ["classification", "mask_strategy", "is_active", "label"].forEach((k) => body[k] !== undefined && (f[k] = body[k]));
    return ok(presentField(f), "Field updated");
  }],

  ["post", "tenant/config/sensitive-fields/:id/access", ({ params, body }) => {
    const f = sensitiveFields.find((x) => String(x.id) === String(params.id));
    if (!f) return notFound(`Sensitive field ${params.id} was not found.`);
    if (!roles.some((r) => r.name === body.role)) return invalid({ role: ["The selected role is invalid."] });

    const list = (fieldAccess[f.field_key] ??= []);
    const granted = !!body.granted;
    if (granted && !list.includes(body.role)) list.push(body.role);
    if (!granted && list.includes(body.role)) list.splice(list.indexOf(body.role), 1);

    return ok(
      { field: f.field_key, role: body.role, granted },
      granted ? `${body.role} can now see ${f.label}` : `${body.role} can no longer see ${f.label}`
    );
  }],

  // ---- Approvals inbox ----
  ["get", "tenant/approvals", ({ query }) => {
    const q = String(query.search ?? "").toLowerCase();
    return pageOf(requests
      .filter((r) => !query.status || r.status === query.status)
      .filter((r) => !query.workflow || [].concat(query.workflow).includes(r.workflow.code))
      .filter((r) => !q || [r.reference, r.subject.label, r.requested_by].some((v) => String(v).toLowerCase().includes(q)))
      .sort((a, b) => b.created_at.localeCompare(a.created_at)), query);
  }],

  ["get", "tenant/approvals/summary", () => {
    const mine = requests.filter((r) => r.requested_by === ME_NAME);
    const inbox = requests.filter(waitingOnMe);
    const month = new Date(); month.setDate(1); month.setHours(0, 0, 0, 0);
    return ok({
      waiting_on_me: inbox.length,
      past_sla: inbox.filter((r) => currentStep(r)?.is_overdue).length,
      my_open: mine.filter((r) => r.status === "pending").length,
      returned_to_me: mine.filter((r) => r.status === "returned").length,
      decided_this_month: requests.filter((r) => ["approved", "rejected"].includes(r.status) && (!r.completed_at || new Date(r.completed_at) >= month)).length,
    });
  }],

  ["get", "tenant/approvals/inbox", ({ query }) => {
    const list = requests
      .filter(waitingOnMe)
      .filter((r) => !query.workflow || [].concat(query.workflow).includes(r.workflow.code))
      .filter((r) => !query.module || r.workflow.module === query.module)
      .sort((a, b) => a.created_at.localeCompare(b.created_at));
    const { data, ...page } = paginate(list.map(presentRequest), query);
    return ok({ data, links: {}, meta: { current_page: page.current_page, from: page.from, last_page: page.last_page, per_page: page.per_page, to: page.to, total: page.total } });
  }],

  ["get", "tenant/approvals/mine", ({ query }) => {
    const list = requests
      .filter((r) => r.requested_by === "Jane Wanjiku")
      .filter((r) => !query.status || r.status === query.status)
      .filter((r) => !query.workflow || [].concat(query.workflow).includes(r.workflow.code))
      .filter((r) => !query.module || r.workflow.module === query.module)
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
    const { data, ...page } = paginate(list.map(presentRequest), query);
    return ok({ data, links: {}, meta: { current_page: page.current_page, from: page.from, last_page: page.last_page, per_page: page.per_page, to: page.to, total: page.total } });
  }],

  ["get", "tenant/approvals/:id", ({ params }) => {
    const r = findRequest(params.id);
    return r ? ok(presentRequest(r)) : notFound(`Approval request ${params.id} was not found.`);
  }],

  ["post", "tenant/approvals/:id/approve", ({ params, body }) => decide(params.id, "approved", body.comments, "Approved and applied")],
  ["post", "tenant/approvals/:id/reject", ({ params, body }) => decide(params.id, "rejected", body.comments, "Rejected")],
  ["post", "tenant/approvals/:id/return", ({ params, body }) => {
    if (!String(body.comments ?? "").trim()) return invalid({ comments: ["Say what needs changing, so the person who raised it knows what to do."] });
    return decide(params.id, "returned", body.comments, "Sent back for changes");
  }],
  ["post", "tenant/approvals/:id/cancel", ({ params, body }) => decide(params.id, "cancelled", body.reason, "Request cancelled")],
];
